/**
 * API Client for Device Commands
 * Handles all API calls for device control functionality
 */
import api from './api';

export interface CommandResponse {
  commandId: number;
  status: string;
  issuedAt: string;
}

export interface CommandStatus {
  id: number;
  companyId: number;
  deviceId: number;
  command: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  issuedBy: string;
  issuedAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: string;
  resultImage?: string; // Base64 encoded
  exitCode?: number;
}

export interface TerminalSessionResponse {
  sessionId: number;
  status: string;
  createdAt: string;
}

export interface TerminalCommandResponse {
  commandId: number;
  status: string;
  issuedAt: string;
}

export interface TerminalCommandStatus {
  id: number;
  command: string;
  status: 'pending' | 'running' | 'completed';
  issuedAt: string;
  completedAt?: string;
  output?: string;
  exitCode?: number;
}

class DeviceCommandsApi {
  /**
   * Issue a quick action command to a device
   */
  async issueCommand(deviceId: number, command: string): Promise<CommandResponse> {
    const response = await api.post(`/devices/${deviceId}/command`, { command });
    return response.data;
  }

  /**
   * Get command status by ID (for polling)
   */
  async getCommandStatus(commandId: number): Promise<CommandStatus> {
    const response = await api.get(`/commands/${commandId}`);
    return response.data;
  }

  /**
   * Get command history for a device
   */
  async getCommandHistory(deviceId: number, limit: number = 50): Promise<CommandStatus[]> {
    const response = await api.get(`/devices/${deviceId}/commands?limit=${limit}`);
    return response.data;
  }

  /**
   * Cancel a pending command
   */
  async cancelCommand(commandId: number): Promise<void> {
    await api.delete(`/commands/${commandId}`);
  }

  /**
   * Create a terminal session for a device
   */
  async createTerminalSession(deviceId: number): Promise<TerminalSessionResponse> {
    const response = await api.post(`/devices/${deviceId}/terminal/session`);
    return response.data;
  }

  /**
   * Send a command to a terminal session
   */
  async sendTerminalCommand(
    sessionId: number,
    command: string
  ): Promise<TerminalCommandResponse> {
    const response = await api.post(`/terminal/sessions/${sessionId}/command`, { command });
    return response.data;
  }

  /**
   * Get all commands for a terminal session
   */
  async getTerminalCommands(sessionId: number): Promise<TerminalCommandStatus[]> {
    const response = await api.get(`/terminal/sessions/${sessionId}/commands`);
    return response.data.commands;
  }

  /**
   * Close a terminal session
   */
  async closeTerminalSession(sessionId: number): Promise<void> {
    await api.delete(`/terminal/sessions/${sessionId}`);
  }

  /**
   * Poll for command completion
   * Returns a promise that resolves when command is complete
   */
  async pollCommandCompletion(
    commandId: number,
    maxAttempts: number = 60,
    intervalMs: number = 1000
  ): Promise<CommandStatus> {
    for (let i = 0; i < maxAttempts; i++) {
      const status = await this.getCommandStatus(commandId);

      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Command polling timed out');
  }
}

// Export singleton instance
export const deviceCommandsApi = new DeviceCommandsApi();
